import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

const plans = [
    {
        name: "Starter",
        price: "R$ 197",
        description: "Ideal para pequenos negócios ou novos empreendedores.",
        features: ["Até 100 pedidos/mês", "Dashboard Básico", "Gestão de Mesas", "Suporte via Email"],
        highlight: false
    },
    {
        name: "Pro",
        price: "R$ 497",
        description: "O plano completo para quem busca escala e automação.",
        features: ["Pedidos Ilimitados", "CRM Avançado", "Integração iFood & Whats", "Suporte Prioritário 24/7", "Relatórios Financeiros"],
        highlight: true
    },
    {
        name: "Enterprise",
        price: "Sob consulta",
        description: "Soluções personalizadas para redes e franquias.",
        features: ["Múltiplas Unidades", "API Customizada", "Gerente de Conta", "Treinamento VIP", "SLA Garantido"],
        highlight: false
    }
];

interface PricingProps {
    onOpenDemo: (title?: string) => void;
}

const Pricing: React.FC<PricingProps> = ({ onOpenDemo }) => {
    return (
        <section id="planos" className="py-32 px-6 bg-background relative">
            {/* Background glow for the highlighted section */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px] -z-10" />

            <div className="max-w-[1400px] mx-auto space-y-20">
                <div className="text-center space-y-6">
                    <h2 className="text-4xl md:text-6xl font-black text-white tracking-tighter">Escolha o seu plano.</h2>
                    <p className="text-xl text-secondary max-w-2xl mx-auto">
                        Transparência total. Sem taxas escondidas. Cancele quando quiser.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
                    {plans.map((plan, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                            className={`relative flex flex-col p-10 rounded-[40px] border ${plan.highlight
                                    ? 'bg-[#151515] border-primary/30 shadow-2xl scale-105 z-10'
                                    : 'bg-[#0f0f0f] border-white/5 opacity-80'
                                }`}
                        >
                            {plan.highlight && (
                                <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-primary text-white text-xs font-black uppercase tracking-widest px-6 py-2 rounded-full">
                                    Mais Popular
                                </div>
                            )}

                            <div className="space-y-4 mb-10">
                                <h3 className="text-2xl font-bold text-white/50">{plan.name}</h3>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-5xl font-black text-white">{plan.price}</span>
                                    {plan.price.includes('R$') && <span className="text-secondary font-bold">/mês</span>}
                                </div>
                                <p className="text-secondary font-medium">{plan.description}</p>
                            </div>

                            <div className="space-y-4 flex-1 mb-10">
                                {plan.features.map(feature => (
                                    <div key={feature} className="flex items-center gap-3">
                                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center">
                                            <Check className="w-3 h-3 text-primary" />
                                        </div>
                                        <span className="text-white/80 font-medium">{feature}</span>
                                    </div>
                                ))}
                            </div>

                            <button 
                                onClick={() => {
                                    if (plan.name === 'Enterprise') {
                                        onOpenDemo(`Plano Enterprise - ${plan.name}`);
                                    } else {
                                        window.location.href = '/admin/login';
                                    }
                                }}
                                className={`w-full py-4 rounded-full font-bold text-lg transition-all active:scale-95 ${plan.highlight
                                    ? 'bg-primary text-white hover:bg-primary-hover shadow-lg shadow-primary/20'
                                    : 'bg-white/5 text-white hover:bg-white/10'
                                }`}>
                                {plan.name === 'Enterprise' ? 'Falar com especialista' : 'Começar agora'}
                            </button>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Pricing;
